import { areArraysEqual, isDeepEqual } from 'utils/Compare';

// Stored documents may also carry a `reserved` boolean. It is deliberately
// not modeled: it was a frontend invention that, in AdHub 2.x, hid
// properties and locked their names, and nothing has read it since that code
// was removed in 2022. Whether a property is special is decided by its name
// and its place in the tree - the build depends on the backend's well-known
// properties (functions/src/dyndata/wellknown.ts) whatever the flag says - so
// it is not something a property can carry around with it. Where it still
// turns up it is ignored, and the live properties no longer write it back.
export interface DynamicPropertyData {
  name: string;
  type: string;
  value: any;
}

export function isDynamicPropertyData(x: unknown): x is DynamicPropertyData {
  return (
    x != null &&
    typeof x === 'object' &&
    'name' in x &&
    typeof x.name === 'string' &&
    x.name.length > 0 &&
    'type' in x &&
    typeof x.type === 'string' &&
    x.type.length > 0 &&
    'value' in x
  );
}

export function areDynamicPropertyDataEqual(
  a: DynamicPropertyData,
  b: DynamicPropertyData
): boolean {
  if (a === b) return true;
  if (a.name !== b.name) return false;
  if (a.type !== b.type) return false;

  if (Array.isArray(a.value) && Array.isArray(b.value)) {
    return areDynamicPropertyDataArraysEqual(a.value, b.value);
  }
  return isDeepEqual(a.value, b.value);
}

export function areDynamicPropertyDataArraysEqual(
  a: DynamicPropertyData[] | null | undefined,
  b: DynamicPropertyData[] | null | undefined
): boolean {
  if (!a) return !b;
  if (!b) return false;
  return areArraysEqual(a, b, areDynamicPropertyDataEqual);
}

function cloneDynamicPropertyDataValue(value: any): any {
  if (value == null || typeof value !== 'object') {
    // null or primitive value; just return it
    return value;
  }

  if (Array.isArray(value)) {
    // Array of child properties
    return Array.from(value, (v) => cloneDynamicPropertyData(v));
  }

  console.warn('[cloneDynamicPropertyData] Unsupported value: %O', value);
  // But gotta do something, so:
  return JSON.parse(JSON.stringify(value));
}

export function cloneDynamicPropertyData({
  name,
  type,
  value,
}: DynamicPropertyData): DynamicPropertyData {
  return { name, type, value: cloneDynamicPropertyDataValue(value) };
}

export function cloneDynamicPropertyDataArray(
  dataArray: DynamicPropertyData[]
): DynamicPropertyData[] {
  return dataArray.map((d) => cloneDynamicPropertyData(d));
}
